import { useCallback, useMemo, useState } from 'react';

import isNil from 'lodash/isNil';

import settings from '@src/conf';

const STORAGE_KEY = 'ReactStorage__Authentication';

function useJwtStorage() {
  const parse = () => {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? JSON.parse(value) : null;
  };

  const [credentials, setCredentials] = useState(parse);

  // Date.now() in useMemo computation is flagged by react-hooks/purity. Kept intentionally — auth is currently
  // commented out across the app (see App.jsx `// AUTH:` markers); this scaffolding is preserved for re-enablement.
  // Revisit when auth is restored — proper fix is a periodic state update via useEffect+setInterval, not render-time time math.
  const isAccessTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.accessTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    // eslint-disable-next-line react-hooks/purity
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isRefreshTokenExpired = useMemo(() => {
    const expiredAt = credentials?.metadata?.refreshTokenExpiredAt;
    if (isNil(expiredAt)) {
      return true;
    }
    // eslint-disable-next-line react-hooks/purity
    const delta = expiredAt - Date.now();
    return delta < settings.AUTH.JWT.EXPIRED_BUFFER;
  }, [credentials]);

  const isExpired = useMemo(() => isAccessTokenExpired || isRefreshTokenExpired, [isAccessTokenExpired, isRefreshTokenExpired]);
  const isAuthenticated = useMemo(() => !isExpired && Boolean(credentials), [isExpired, credentials]);

  const set = useCallback((payload) => {
    // SECURITY: never persist the refresh token to localStorage (XSS-stealable).
    // The refresh token is delivered/stored as an httpOnly cookie by the backend
    // (apps/auth: refresh_token cookie). Only the short-lived access token +
    // metadata live here, and the full payload (incl. access) stays in memory.
    const { refresh: _refresh, ...persistable } = payload || {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    setCredentials(payload);
  }, []);

  const remove = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(null));
    setCredentials(null);
  }, []);


  return {
    set,
    remove,
    parse,
    credentials,
    isAuthenticated,
  };
}

export { useJwtStorage };
